/**
 * Streaming MP3 player using Media Source Extensions (MSE).
 *
 * Pipes a fetch ReadableStream of MP3 bytes into a SourceBuffer,
 * starts playback as soon as enough data is buffered to decode.
 *
 * Browser support:
 *   - Chrome / Edge / Firefox / Android Chrome: full MSE support for audio/mpeg
 *   - iOS Safari (WKWebView): NO MediaSource on the window object — falls back to
 *     full-download blob playback (no streaming, plays after full response received)
 */

const MIME = 'audio/mpeg';

/**
 * Play a brief, soft "your turn" chime after Claude finishes speaking.
 * Uses Web Audio API — no asset dependency. Silently no-ops if unavailable
 * (e.g., AudioContext locked on iOS before user gesture).
 */
export function playTurnTone() {
  try {
    if (typeof window === 'undefined') return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    // Two-note chime: higher then slightly lower — conversational "over to you"
    const schedule = [
      { freq: 880, start: 0,    dur: 0.14, peak: 0.07 },
      { freq: 660, start: 0.10, dur: 0.18, peak: 0.06 },
    ];
    const now = ctx.currentTime;
    for (const { freq, start, dur, peak } of schedule) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(peak, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur);
    }
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 500);
  } catch { /* noop */ }
}

export function isMseSupported() {
  return typeof window !== 'undefined'
    && typeof window.MediaSource !== 'undefined'
    && window.MediaSource.isTypeSupported(MIME);
}

/**
 * Play a streaming MP3 response.
 * @param {Response} response - fetch Response with audio/mpeg body
 * @param {object} opts
 * @param {(stage: string, info: object) => void} [opts.onTiming] - timing callbacks
 * @param {() => void} [opts.onEnded]
 * @param {(err: Error) => void} [opts.onError]
 * @returns {Promise<{audio: HTMLAudioElement, stop: () => void}>}
 */
export async function playStreamingMp3(response, opts = {}) {
  const t0 = performance.now();
  const onTiming = opts.onTiming || (() => {});
  const onEnded = opts.onEnded || (() => {});
  const onError = opts.onError || ((e) => console.error(e));

  if (!isMseSupported()) {
    return playFullDownload(response, { t0, onTiming, onEnded, onError });
  }

  const mediaSource = new MediaSource();
  const audio = new Audio();
  audio.src = URL.createObjectURL(mediaSource);

  const sourceOpenP = new Promise((resolve, reject) => {
    mediaSource.addEventListener('sourceopen', resolve, { once: true });
    mediaSource.addEventListener('error', reject, { once: true });
  });

  await sourceOpenP;
  const sourceBuffer = mediaSource.addSourceBuffer(MIME);
  sourceBuffer.mode = 'sequence';

  const queue = [];
  let upstreamDone = false;
  let firstAudioReported = false;

  function pump() {
    if (sourceBuffer.updating || queue.length === 0) {
      if (upstreamDone && !sourceBuffer.updating && queue.length === 0
          && mediaSource.readyState === 'open') {
        try { mediaSource.endOfStream(); } catch { /* already ended */ }
      }
      return;
    }
    const chunk = queue.shift();
    try {
      sourceBuffer.appendBuffer(chunk);
    } catch (e) {
      onError(e);
    }
  }

  sourceBuffer.addEventListener('updateend', () => {
    if (!firstAudioReported && audio.buffered.length > 0) {
      firstAudioReported = true;
      onTiming('first-buffer', { ms: performance.now() - t0 });
      audio.play().then(() => {
        onTiming('playing', { ms: performance.now() - t0 });
      }).catch((e) => {
        // Autoplay block: caller must invoke this from a user gesture
        onError(e);
      });
    }
    pump();
  });

  audio.addEventListener('ended', () => {
    onTiming('ended', { ms: performance.now() - t0 });
    URL.revokeObjectURL(audio.src);
    onEnded();
  });

  // Pump bytes from upstream
  const reader = response.body.getReader();
  let totalBytes = 0;
  let firstByteReported = false;

  (async () => {
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!firstByteReported) {
          firstByteReported = true;
          onTiming('first-byte', { ms: performance.now() - t0 });
        }
        totalBytes += value.byteLength;
        queue.push(value);
        pump();
      }
      upstreamDone = true;
      onTiming('upstream-done', { ms: performance.now() - t0, bytes: totalBytes });
      pump();
    } catch (e) {
      onError(e);
    }
  })();

  function stop() {
    try { audio.pause(); } catch { /* noop */ }
    try { reader.cancel(); } catch { /* noop */ }
    try { if (mediaSource.readyState === 'open') mediaSource.endOfStream(); } catch { /* noop */ }
    try { URL.revokeObjectURL(audio.src); } catch { /* noop */ }
  }

  return { audio, stop };
}

/**
 * Chained streaming MP3 player — plays a queue of Response streams back-to-back
 * into a single MediaSource, gaplessly. Used for sentence-chunk TTS where each
 * sentence is a separate /voice/speak request but should play as one utterance.
 *
 * Usage:
 *   const player = await createChainedPlayer({ onEnded, onError });
 *   player.enqueue(speakStream('First sentence.'));
 *   player.enqueue(speakStream('Second sentence.'));
 *   player.end();   // signals no more responses coming
 *   player.stop();  // abort all playback + cancel in-flight fetches
 *
 * Returns null if MSE is not supported (caller must fall back).
 */
export async function createChainedPlayer(opts = {}) {
  if (!isMseSupported()) return null;

  const t0 = performance.now();
  const onTiming = opts.onTiming || (() => {});
  const onEnded = opts.onEnded || (() => {});
  const onError = opts.onError || ((e) => console.error(e));

  const mediaSource = new MediaSource();
  const audio = new Audio();
  audio.src = URL.createObjectURL(mediaSource);

  await new Promise((resolve, reject) => {
    mediaSource.addEventListener('sourceopen', resolve, { once: true });
    mediaSource.addEventListener('error', reject, { once: true });
  });

  const sourceBuffer = mediaSource.addSourceBuffer(MIME);
  sourceBuffer.mode = 'sequence';

  const byteQueue = [];
  const responseQueue = []; // Array<Promise<Response>>
  let currentReader = null;
  let endRequested = false;
  let stopped = false;
  let firstAudioReported = false;
  let draining = false;

  function pumpBytes() {
    if (stopped) return;
    if (sourceBuffer.updating || byteQueue.length === 0) {
      if (endRequested && !sourceBuffer.updating
          && byteQueue.length === 0
          && responseQueue.length === 0
          && !currentReader
          && !draining
          && mediaSource.readyState === 'open') {
        try { mediaSource.endOfStream(); } catch { /* already ended */ }
      }
      return;
    }
    const chunk = byteQueue.shift();
    try { sourceBuffer.appendBuffer(chunk); }
    catch (e) { onError(e); }
  }

  sourceBuffer.addEventListener('updateend', () => {
    if (!firstAudioReported && audio.buffered.length > 0) {
      firstAudioReported = true;
      onTiming('first-buffer', { ms: performance.now() - t0 });
      audio.play()
        .then(() => onTiming('playing', { ms: performance.now() - t0 }))
        .catch(onError);
    }
    pumpBytes();
  });

  audio.addEventListener('ended', () => {
    onTiming('ended', { ms: performance.now() - t0 });
    URL.revokeObjectURL(audio.src);
    onEnded();
  });

  async function drain() {
    if (draining) return;
    draining = true;
    try {
      while (!stopped && responseQueue.length > 0) {
        const responsePromise = responseQueue.shift();
        let response;
        try { response = await responsePromise; }
        catch (e) { onError(e); continue; }
        if (stopped) return;
        currentReader = response.body.getReader();
        try {
          while (!stopped) {
            const { value, done } = await currentReader.read();
            if (done) break;
            byteQueue.push(value);
            pumpBytes();
          }
        } catch (e) {
          onError(e);
        } finally {
          try { currentReader.releaseLock?.(); } catch { /* noop */ }
          currentReader = null;
        }
      }
    } finally {
      draining = false;
      pumpBytes();
    }
  }

  function enqueue(responsePromise) {
    if (stopped || endRequested) return;
    responseQueue.push(responsePromise);
    drain();
  }

  function end() {
    endRequested = true;
    pumpBytes();
  }

  function stop() {
    stopped = true;
    try { audio.pause(); } catch { /* noop */ }
    try { if (currentReader) currentReader.cancel(); } catch { /* noop */ }
    try { if (mediaSource.readyState === 'open') mediaSource.endOfStream(); } catch { /* noop */ }
    try { URL.revokeObjectURL(audio.src); } catch { /* noop */ }
    responseQueue.length = 0;
    byteQueue.length = 0;
  }

  return { audio, enqueue, end, stop };
}

/**
 * Fallback for browsers without MSE (notably iOS Safari).
 * Buffers the full response, then plays.
 */
async function playFullDownload(response, { t0, onTiming, onEnded, onError }) {
  const chunks = [];
  const reader = response.body.getReader();
  let firstByteReported = false;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    if (!firstByteReported) {
      firstByteReported = true;
      onTiming('first-byte', { ms: performance.now() - t0 });
    }
    chunks.push(value);
  }
  onTiming('upstream-done', { ms: performance.now() - t0 });

  const blob = new Blob(chunks, { type: MIME });
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);

  audio.addEventListener('ended', () => {
    onTiming('ended', { ms: performance.now() - t0 });
    URL.revokeObjectURL(url);
    onEnded();
  });

  try {
    await audio.play();
    onTiming('playing', { ms: performance.now() - t0 });
  } catch (e) {
    onError(e);
  }

  return {
    audio,
    stop: () => { try { audio.pause(); } catch { /* noop */ } URL.revokeObjectURL(url); },
  };
}
